/**
 * apps/automations-portal/src/features/admin/DeployBlockWizard.tsx
 *
 * 3-step dialog: pick a block, configure it, review + deploy.
 */
import { useEffect, useState } from 'react';
import { Check, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/sonner';
import { adminApi, ApiError, isNotImplemented } from '@/lib/api';
import type { Block } from '@/lib/types';
import { ConfigForm, toConfigObject, type ConfigValue } from './ConfigForm';

type Step = 'pick' | 'configure' | 'review';

export interface DeployBlockWizardProps {
  clientId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDeployed: () => void;
}

export function DeployBlockWizard({ clientId, open, onOpenChange, onDeployed }: DeployBlockWizardProps) {
  const [step, setStep] = useState<Step>('pick');
  const [blocks, setBlocks] = useState<Block[] | null>(null);
  const [blocksError, setBlocksError] = useState<string | null>(null);
  const [selectedBlock, setSelectedBlock] = useState<Block | null>(null);
  const [configValue, setConfigValue] = useState<ConfigValue>({});
  // Field-level problems the server found. Cleared as soon as anything is
  // edited, so a stale red message never sits under a field just fixed.
  const [serverIssues, setServerIssues] = useState<{ key: string; message: string }[]>([]);
  const [configValid, setConfigValid] = useState(false);
  const [deploying, setDeploying] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStep('pick');
    setSelectedBlock(null);
    setConfigValue({});
    setServerIssues([]);
    setConfigValid(false);
    setBlocksError(null);
    setBlocks(null);

    adminApi
      .listBlocks()
      .then(setBlocks)
      .catch((err) => {
        if (isNotImplemented(err)) {
          setBlocksError('The block catalogue is coming soon — there is nothing to deploy yet.');
        } else {
          setBlocksError('Could not load the automation catalogue. Try again shortly.');
        }
      });
  }, [open]);

  function pickBlock(block: Block) {
    setSelectedBlock(block);
    setStep('configure');
  }

  async function handleDeploy() {
    if (!selectedBlock) return;
    setDeploying(true);
    try {
      await adminApi.deployBlock(clientId, {
        blockId: selectedBlock.id,
        config: toConfigObject(selectedBlock.fields ?? [], configValue),
      });
      toast('Deployed', { description: `${selectedBlock.name} is now deploying for this client.` });
      onDeployed();
      onOpenChange(false);
    } catch (err) {
      if (isNotImplemented(err)) {
        toast('Deployment coming soon', {
          description: 'The automation engine cannot accept deployments yet — this will work once it lands.',
        });
      } else if (err instanceof ApiError && err.issues?.length) {
        // Send them back to the fields that need fixing rather than leaving a
        // toast on a review screen that shows no way to act on it.
        setServerIssues(err.issues);
        setStep('configure');
        toast('Check the highlighted fields', { description: err.message });
      } else if (err instanceof ApiError) {
        toast('Could not deploy', { description: err.message });
      } else {
        toast('Could not deploy', { description: 'Something went wrong. Try again.' });
      }
    } finally {
      setDeploying(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Deploy a block</DialogTitle>
          <DialogDescription>
            {step === 'pick' && 'Choose an automation to deploy for this client.'}
            {step === 'configure' && selectedBlock && `Configure ${selectedBlock.name}.`}
            {step === 'review' && 'Review the configuration before deploying.'}
          </DialogDescription>
        </DialogHeader>

        {step === 'pick' && (
          <div className="flex max-h-96 flex-col gap-2 overflow-y-auto">
            {blocksError && <p className="text-sm text-muted-foreground">{blocksError}</p>}
            {!blocksError && !blocks && (
              <div className="flex flex-col gap-2">
                <Skeleton className="h-16 w-full" />
                <Skeleton className="h-16 w-full" />
              </div>
            )}
            {blocks?.length === 0 && (
              <p className="text-sm text-muted-foreground">No automations are in the catalogue yet.</p>
            )}
            {blocks?.map((block) => (
              <button
                key={block.id}
                type="button"
                onClick={() => pickBlock(block)}
                className="flex flex-col gap-1 rounded-md border border-border p-3 text-left transition-colors hover:border-primary hover:bg-accent"
              >
                <span className="text-sm font-medium">{block.name}</span>
                <span className="text-xs text-muted-foreground">{block.description}</span>
              </button>
            ))}
          </div>
        )}

        {step === 'configure' && selectedBlock && (
          <div className="max-h-96 overflow-y-auto pr-1">
            <ConfigForm
              fields={selectedBlock.fields ?? []}
              value={configValue}
              serverIssues={serverIssues}
              onChange={(next) => {
                setServerIssues([]);
                setConfigValue(next);
              }}
              onValidChange={setConfigValid}
            />
          </div>
        )}

        {step === 'review' && selectedBlock && (
          <div className="flex flex-col gap-3">
            <div>
              <p className="text-sm font-medium">{selectedBlock.name}</p>
              <p className="text-xs text-muted-foreground">{selectedBlock.description}</p>
            </div>
            <dl className="flex flex-col gap-2 rounded-md border border-border bg-muted/40 p-3 text-sm">
              {Object.entries(toConfigObject(selectedBlock.fields ?? [], configValue)).map(([key, val]) => {
                const field = (selectedBlock.fields ?? []).find((f) => f.key === key);
                return (
                  <div key={key} className="flex justify-between gap-4">
                    <dt className="text-muted-foreground">{field?.label ?? key}</dt>
                    <dd className="text-right font-medium">
                      {Array.isArray(val) ? (val.length > 0 ? val.join(', ') : '—') : String(val ?? '—')}
                    </dd>
                  </div>
                );
              })}
            </dl>
          </div>
        )}

        <DialogFooter>
          {step !== 'pick' && (
            <Button
              type="button"
              variant="outline"
              onClick={() => setStep(step === 'review' ? 'configure' : 'pick')}
              disabled={deploying}
            >
              <ChevronLeft className="h-4 w-4" />
              Back
            </Button>
          )}
          {step === 'configure' && (
            <Button type="button" onClick={() => setStep('review')} disabled={!configValid}>
              Review
              <ChevronRight className="h-4 w-4" />
            </Button>
          )}
          {step === 'review' && (
            <Button type="button" onClick={handleDeploy} disabled={deploying}>
              {deploying ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
              Deploy
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
